import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assetSchemaSelectorByType } from '@innovyze/lib_asset_schema_store';
import { Box, Collapse, Paper, Skeleton, Typography } from '@mui/material';
import { getRiskAssetCount } from '@Actions';
import { selectRiskAssetCount } from '@Selectors/RiskAssetCount.selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { TFunc } from '@Translations/types';
import { Category } from '@Types/category.types';
import type { AssetCountState } from '@Reducers/Risk.reducers';
import { RiskSetupDraft } from '@Components/RiskGrade/redesign/riskSetupDraft.types';
import { cardSx } from '@Components/RiskGrade/redesign/cardSx';

export interface PreRunSummaryProps {
	draft: RiskSetupDraft;
	assetType: string;
	assetFilterId?: string;
	lofCategories: Category[];
	cofCategories: Category[];
}

const show = (value: number): string =>
	Number.isNaN(value) ? '–' : String(value);

const activeComponents = (category: Category) =>
	category.components.filter(c => c.select && c.weighting !== 0);

const methodLabel = (draft: RiskSetupDraft, t: TFunc): string => {
	if (draft.riskType === 'matrix') return t('5×5 Matrix');
	if (draft.riskMethod === 'multiplied')
		return t('Linear · Multiply (LoF × CoF)');
	if (draft.riskMethod === 'added') return t('Linear · Add (LoF + CoF)');
	return t('Linear');
};

// Typography and spacing below are copied from the I360A-2067 spike's
// PreRunSummary so the panel reads the same (semi-bold values, grey eyebrows).
const Row = ({
	label,
	value,
}: {
	label: React.ReactNode;
	value: React.ReactNode;
}) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'baseline',
			gap: 2,
			mb: 0.75,
		}}>
		<Typography sx={{ fontSize: 13, color: '#5b6472' }}>{label}</Typography>
		<Typography
			sx={{
				fontSize: 13,
				fontWeight: 600,
				color: '#1b2733',
				textAlign: 'right',
			}}>
			{value}
		</Typography>
	</Box>
);

const Eyebrow = ({ title }: { title: string }) => (
	<Typography
		sx={{
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: '0.06em',
			textTransform: 'uppercase',
			color: '#97a0ac',
			mt: 2.5,
			mb: 1,
		}}>
		{title}
	</Typography>
);

// ▸ collapsed / ▾ expanded, small and grey.
const Chevron = ({ open }: { open: boolean }) => (
	<Box
		component="span"
		data-cy="pre-run-model-chevron"
		aria-hidden
		sx={{ fontSize: 10, color: '#97a0ac', lineHeight: 1 }}>
		{open ? '▾' : '▸'}
	</Box>
);

const CategoryBreakdown = ({ categories }: { categories: Category[] }) => (
	<Box sx={{ mt: 1, p: 1.5, background: '#f7f8fa', borderRadius: '8px' }}>
		{categories.map((category, i) => (
			<Box
				key={category.name}
				sx={{ mb: i < categories.length - 1 ? 1.5 : 0 }}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'baseline',
						gap: 1,
					}}>
					<Typography
						sx={{
							fontSize: 12.5,
							fontWeight: 700,
							color: '#1b2733',
						}}>
						{category.name}
					</Typography>
					<Typography
						sx={{
							fontSize: 12,
							fontWeight: 600,
							color: '#6b7482',
						}}>
						{`${show(category.weighting)}%`}
					</Typography>
				</Box>
				<Box
					data-cy="pre-run-category-components"
					sx={{
						mt: 0.5,
						ml: 0.25,
						pl: 1.25,
						borderLeft: '2px solid #e4e8ee',
					}}>
					{activeComponents(category).map(component => (
						<Box
							key={component.name}
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'baseline',
								gap: 1,
								py: 0.3,
							}}>
							<Typography
								sx={{ fontSize: 12.5, color: '#5b6472' }}>
								{component.name}
							</Typography>
							<Typography
								sx={{ fontSize: 12.5, color: '#97a0ac' }}>
								{category.useHighestScore
									? '--'
									: `${show(component.weighting)}%`}
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
		))}
	</Box>
);

const ModelRow = ({
	id,
	label,
	categories,
}: {
	id: string;
	label: string;
	categories: Category[];
}) => {
	const { t } = useGlobalization();
	const [open, setOpen] = useState(false);
	const used = categories.filter(
		c => c.weighting !== 0 && activeComponents(c).length > 0,
	);
	const componentCount = used.reduce(
		(sum, c) => sum + activeComponents(c).length,
		0,
	);
	const empty = used.length === 0;
	const summary = empty
		? t('none yet')
		: [
				t('{{count}} categories', { count: used.length }),
				t('{{count}} components', { count: componentCount }),
		  ].join(' · ');
	return (
		<Box sx={{ mb: 0.75 }}>
			<Box
				component="button"
				type="button"
				data-cy={`pre-run-model-${id}`}
				aria-expanded={empty ? undefined : open}
				disabled={empty}
				onClick={() => setOpen(o => !o)}
				sx={{
					width: '100%',
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
					gap: 2,
					border: 'none',
					background: 'transparent',
					p: 0,
					font: 'inherit',
					cursor: empty ? 'default' : 'pointer',
					textAlign: 'left',
				}}>
				<Box
					component="span"
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 0.75,
					}}>
					{!empty && <Chevron open={open} />}
					<Typography
						component="span"
						sx={{
							fontSize: 13,
							fontWeight: 600,
							color: '#1b2733',
						}}>
						{label}
					</Typography>
				</Box>
				<Typography
					component="span"
					sx={{
						fontSize: 13,
						fontWeight: 600,
						color: empty ? '#97a0ac' : '#1b2733',
						textAlign: 'right',
					}}>
					{summary}
				</Typography>
			</Box>
			<Collapse in={open && !empty}>
				<CategoryBreakdown categories={used} />
			</Collapse>
		</Box>
	);
};

// The number in "Assets in scope": a muted placeholder while loading, a muted
// message if the count failed, nothing before the first fetch.
const AssetCount = ({ status, assetCount }: AssetCountState) => {
	const { t } = useGlobalization();
	if (status === 'loading')
		return (
			<Skeleton
				data-cy="pre-run-asset-count-loading"
				variant="text"
				width={60}
				sx={{ fontSize: 22, lineHeight: 1.1 }}
			/>
		);
	if (status === 'error')
		return (
			<Typography
				data-cy="pre-run-asset-count-error"
				sx={{ fontSize: 13, color: '#97a0ac' }}>
				{
					// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
					t("Couldn't load count")
				}
			</Typography>
		);
	if (status !== 'loaded' || assetCount === null) return null;
	return (
		<Typography
			data-cy="pre-run-asset-count"
			sx={{
				fontSize: 22,
				fontWeight: 700,
				color: '#0d1520',
				lineHeight: 1.1,
			}}>
			{assetCount.toLocaleString()}
		</Typography>
	);
};

export const PreRunSummary = ({
	draft,
	assetType,
	assetFilterId,
	lofCategories,
	cofCategories,
}: PreRunSummaryProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const { status, assetCount } = selectRiskAssetCount();
	const schema = useSelector(state =>
		assetSchemaSelectorByType(state, assetType),
	);

	useEffect(() => {
		// The placeholder config before load has no asset type — don't 400.
		if (!assetType) return;
		dispatch(getRiskAssetCount({ assetType, assetFilterId }));
	}, [assetType, assetFilterId, dispatch]);

	return (
		<Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
			<Typography
				sx={{ fontSize: 16, fontWeight: 700, color: '#0d1520' }}>
				{t('Pre-run summary')}
			</Typography>
			<Typography
				sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
				{t('Review what will be included when you run the model.')}
			</Typography>

			<Box sx={{ mt: 2, pb: 2, borderBottom: '1px solid #f1f3f6' }}>
				<Typography
					sx={{
						fontSize: 11,
						fontWeight: 700,
						letterSpacing: '0.06em',
						textTransform: 'uppercase',
						color: '#97a0ac',
						mb: 0.5,
					}}>
					{t('Assets in scope')}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
					<AssetCount status={status} assetCount={assetCount} />

					<Typography
						data-cy="pre-run-asset-type"
						sx={{ fontSize: 13, color: '#5b6472' }}>
						{schema?.displayName ?? assetType}
					</Typography>
				</Box>
				<Typography sx={{ fontSize: 12, color: '#97a0ac', mt: 0.25 }}>
					{assetFilterId
						? t('Filtered subset')
						: t('All assets of this type')}
				</Typography>
			</Box>

			<Eyebrow title={t('Method')} />
			<Row label={t('Calculation')} value={methodLabel(draft, t)} />
			{draft.riskType === 'linear' && (
				<Row
					label={t('LoF / CoF weighting')}
					value={`${show(draft.lofWeighting)}% / ${show(
						draft.cofWeighting,
					)}%`}
				/>
			)}

			<Eyebrow title={t('Model')} />
			<ModelRow id="lof" label={t('LoF')} categories={lofCategories} />
			<ModelRow id="cof" label={t('CoF')} categories={cofCategories} />
			<Row
				label={t('Grades')}
				value={t('5 grades (Optimal → Critical)')}
			/>
		</Paper>
	);
};
